/** 服装视频换装 (Apparel Video Replication) — Canonical Generation Card.
 *
 * VistoeraTASK-00161. Reference VIDEO (pacing/camera/pose) + real product
 * images + product description [+ optional model images / BGM] -> new
 * product commercial video. Deliberately its OWN node — never merged into
 * `GenerateTryOnNode` (image garment swap) or `GenerateVideoNode` (generic
 * text/image → video). Kling's own request/response shapes never reach
 * this file — see `app.saas.apparel_video_providers` backend package.
 *
 * Vertical order: HEADER → RESULT (video) → INPUT ASSETS → PARAMETERS →
 * PROMPT(商品描述) → FOOTER. Same collapse/expand contract as every other
 * Generation Card (`canvasNodeShellV2.ts`).
 *
 * V1 scoping (kept honest, not silently pretended complete — see this
 * task's own final report): every input (source_video / product_images /
 * model_images / bgm) is bound via the existing click-to-pick Library
 * pickers only. No react-flow target Handles / drag-connect dataflow
 * wiring in this pass (that needs a `connectedApparelVideoXxx` computed
 * prop in `CanvasBoard.tsx`, mirroring `connectedTryOnPerson`/`connectedTryOnGarment`,
 * which is a real follow-up, not done here). The finished video also has
 * no output Handle for the same reason — the result is downloaded /
 * viewed directly on the card.
 */

import { CloseOutlined, CopyOutlined, DownloadOutlined, HistoryOutlined, LoadingOutlined, ThunderboltOutlined } from '@ant-design/icons'
import type { NodeProps } from '@xyflow/react'
import { Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import { memo, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import { resolveRegenerationDecision } from '../generationRegenerationDecision'
import {
  APPAREL_VIDEO_PRODUCT_DESCRIPTION_MAX_LENGTH,
  APPAREL_VIDEO_REPLICATION_CAPABILITY,
  APPAREL_VIDEO_SUPPORTED_ASPECT_RATIOS,
  APPAREL_VIDEO_SUPPORTED_RESOLUTIONS,
  apparelVideoReplicationOutputContentUrl,
  createApparelVideoReplicationTask,
  getApparelVideoReplicationTask,
  siteAssetVariantUrl,
  videoGenAssetPosterUrl,
  type ApparelVideoReplicationTask,
  type ApparelVideoResolution,
  type ApparelVideoAspectRatio,
  type SaasApiError,
} from '../../../../api'
import CanvasActionPopover from '../CanvasActionPopover'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import {
  buildDefaultProviderSelectionPersistPatch,
  buildExplicitProviderSelectionPatch,
  findProviderOptionById,
  isExplicitProviderSelectionInvalid,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { useSingleOrDoubleClick } from '../useSingleOrDoubleClick'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import MediaLightbox from '../MediaLightbox'
import { downloadCanvasMedia } from '../canvasDownload'
import CanvasVideoPreview from '../CanvasVideoPreview'
import type { FlowNodeData } from '../types'
import { generationNodeCardWidth } from '../generationNodeWidthPresets'

const NODE_COLLAPSED_EMPTY_WIDTH = 260
const NODE_COLLAPSED_EMPTY_HEIGHT = 168
const NODE_CARD_WIDTH = generationNodeCardWidth('apparel_video_replication')
const RESULT_MAX_HEIGHT = 420
const RESULT_EMPTY_HEIGHT = 200
const POLL_INTERVAL_MS = 3000
const MAX_PRODUCT_IMAGES = 5
const MAX_MODEL_IMAGES = 5

interface ApparelImageRef {
  asset_id: string
  picker_item_id?: string
  display_name: string
}

interface ApparelVideoHistoryEntry {
  version: number
  outputAssetId: string | null
  outputWidth?: number | null
  outputHeight?: number | null
  completedAt?: string | null
}

function mapTaskStatus(status: ApparelVideoReplicationTask['status']): string {
  if (status === 'succeeded') return 'COMPLETED'
  if (status === 'failed') return 'FAILED'
  if (status === 'running') return 'PROCESSING'
  if (status === 'queued') return 'QUEUED'
  return 'READY'
}

function appendHistoryEntry(
  history: ApparelVideoHistoryEntry[],
  entry: Omit<ApparelVideoHistoryEntry, 'version' | 'completedAt'>,
): ApparelVideoHistoryEntry[] {
  if (!entry.outputAssetId) return history
  const nextVersion = (history.reduce((max, h) => Math.max(max, h.version), 0) || 0) + 1
  return [...history, { ...entry, version: nextVersion, completedAt: new Date().toISOString() }]
}

function isExecutionActive(status: string): boolean {
  return status === 'SUBMITTING' || status === 'QUEUED' || status === 'PROCESSING'
}

function computeResultHeight(naturalW: number, naturalH: number, cardInnerWidth: number): number {
  if (!naturalW || !naturalH) return RESULT_EMPTY_HEIGHT
  const ratio = naturalW / naturalH
  let height = cardInnerWidth / ratio
  if (height > RESULT_MAX_HEIGHT) height = RESULT_MAX_HEIGHT
  if (height < 160) height = 160
  return Math.round(height)
}

/** Fallback natural size guess from the chosen aspect ratio (before the
 * generated video's own metadata loads) — e.g. "9:16" -> {9,16}. */
function naturalSizeFromAspectRatio(aspectRatio: string): { w: number; h: number } {
  const parts = aspectRatio.split(':').map((p) => Number(p.trim()))
  if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
    return { w: parts[0] * 100, h: parts[1] * 100 }
  }
  return { w: 900, h: 1600 }
}

function ImageChip({
  asset,
  workspaceId,
  disabled,
  onRemove,
}: {
  asset: ApparelImageRef
  workspaceId: string
  disabled?: boolean
  onRemove: () => void
}) {
  const [thumbFailed, setThumbFailed] = useState(false)
  const thumbUrl = workspaceId && !thumbFailed ? siteAssetVariantUrl(workspaceId, asset.asset_id, 'thumbnail') : null
  return (
    <span className="pcv-brief-asset-chip nodrag" title={asset.display_name}>
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt=""
          className="pcv-gen-field-thumb"
          draggable={false}
          onError={() => setThumbFailed(true)}
        />
      ) : null}
      <span className="pcv-brief-asset-name">{asset.display_name}</span>
      {!disabled ? (
        <button type="button" className="pcv-brief-asset-remove" aria-label="移除" onClick={onRemove}>
          <CloseOutlined />
        </button>
      ) : null}
    </span>
  )
}

function ApparelVideoReplicationNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { mediaHeader, regenerate } = useCanvasGenerationI18n()
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const displayLabel = nodeLabel(def.nodeType, def.label)
  const workspaceId = nodeData.workspaceId || ''
  const fields = nodeData.fields || {}

  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const status = (fields.status as string | undefined) ?? 'WAITING'
  const jobId = (fields.jobId as string | undefined) ?? null
  const outputAssetId = (fields.outputAssetId as string | undefined) ?? null
  const outputWidth = (fields.outputWidth as number | undefined) ?? 0
  const outputHeight = (fields.outputHeight as number | undefined) ?? 0
  const errorMessage = (fields.errorMessage as string | null | undefined) ?? null
  const generationHistory = Array.isArray(fields.generationHistory)
    ? (fields.generationHistory as ApparelVideoHistoryEntry[])
    : []

  const sourceVideoAssetId = (fields.sourceVideoAssetId as string | undefined) ?? null
  const sourceVideoLabel = (fields.sourceVideoLabel as string | undefined) ?? null
  const productImages = Array.isArray(fields.productImages) ? (fields.productImages as ApparelImageRef[]) : []
  const modelImages = Array.isArray(fields.modelImages) ? (fields.modelImages as ApparelImageRef[]) : []
  const bgmAssetId = (fields.bgmAssetId as string | undefined) ?? null
  const bgmLabel = (fields.bgmLabel as string | undefined) ?? null
  const productDescription = String(fields.productDescription ?? '')
  const resolution = ((fields.resolution as string) || '720p') as ApparelVideoResolution
  const aspectRatio = ((fields.aspectRatio as string) || '9:16') as ApparelVideoAspectRatio
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | undefined) ?? null

  const [previewOpen, setPreviewOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyBtnEl, setHistoryBtnEl] = useState<HTMLButtonElement | null>(null)
  const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false)
  const [naturalSize, setNaturalSize] = useState({ w: outputWidth || 0, h: outputHeight || 0 })
  const runBtnRef = useRef<HTMLButtonElement | null>(null)
  const preferGeneratingPlaceholderRef = useRef(false)
  const [runHydrating, setRunHydrating] = useState(() => Boolean(jobId && isExecutionActive(status)))
  const submittingRef = useRef(false)
  const pollTimerRef = useRef<number | null>(null)
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields

  const {
    options: providerOptions,
    error: providerOptionsError,
    defaultProviderOptionId,
  } = useCapabilityProviderOptions(
    workspaceId,
    APPAREL_VIDEO_REPLICATION_CAPABILITY,
    'apparel_video_replication',
  )

  const resolvedProviderOptionId = useMemo(
    () =>
      resolveCapabilityProviderOptionId({
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        options: providerOptions,
        serverDefaultProviderOptionId: defaultProviderOptionId,
      }),
    [defaultProviderOptionId, providerOptions, providerSelectionMode, storedProviderOptionId],
  )
  const selectedProvider = useMemo(
    () => findProviderOptionById(providerOptions, resolvedProviderOptionId),
    [providerOptions, resolvedProviderOptionId],
  )
  const explicitProviderInvalid = useMemo(
    () =>
      isExplicitProviderSelectionInvalid({
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        options: providerOptions,
      }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )
  const providerReady =
    Boolean(resolvedProviderOptionId) && !explicitProviderInvalid && (!selectedProvider || isProviderOptionSelectable(selectedProvider))

  const hasSourceVideo = Boolean(sourceVideoAssetId)
  const hasValidProductImages = productImages.length >= 1 && productImages.length <= MAX_PRODUCT_IMAGES
  const descriptionTrimmed = productDescription.trim()
  const hasValidDescription = descriptionTrimmed.length > 0 && productDescription.length <= APPAREL_VIDEO_PRODUCT_DESCRIPTION_MAX_LENGTH
  const isBusy = isExecutionActive(status) || runHydrating
  const canGenerate =
    hasSourceVideo && hasValidProductImages && hasValidDescription && providerReady && !isBusy && !providerOptionsError
  const hasCurrentResult = Boolean(outputAssetId)
  const showResult = Boolean(outputAssetId) || (preferGeneratingPlaceholderRef.current && isBusy)
  const showGenerating = isBusy

  const materialFingerprint = useMemo(
    () =>
      JSON.stringify({
        sourceVideoAssetId,
        productImageIds: productImages.map((a) => a.asset_id),
        modelImageIds: modelImages.map((a) => a.asset_id),
        bgmAssetId,
        productDescription,
        resolution,
        aspectRatio,
        providerOptionId: resolvedProviderOptionId,
      }),
    [aspectRatio, bgmAssetId, modelImages, productDescription, productImages, resolution, resolvedProviderOptionId, sourceVideoAssetId],
  )
  const resultInputFingerprint = (fields.resultInputFingerprint as string | undefined) ?? null
  const needsRegeneration =
    Boolean(fields.needsRegeneration) || (Boolean(resultInputFingerprint) && resultInputFingerprint !== materialFingerprint)

  const collapsedMediaBox = useMemo(() => {
    if (!showResult && !showGenerating) {
      return { width: NODE_COLLAPSED_EMPTY_WIDTH, height: NODE_COLLAPSED_EMPTY_HEIGHT }
    }
    const guess = naturalSizeFromAspectRatio(aspectRatio)
    const w = naturalSize.w || outputWidth || guess.w
    const h = naturalSize.h || outputHeight || guess.h
    const ratio = w / h
    const width = ratio >= 1 ? 320 : Math.max(150, 320 * ratio)
    const height = ratio >= 1 ? Math.max(150, 320 / ratio) : 320
    return { width: Math.round(Math.min(460, width)), height: Math.round(Math.min(460, height)) }
  }, [aspectRatio, naturalSize.h, naturalSize.w, outputHeight, outputWidth, showGenerating, showResult])

  const cardWidth = showExpanded ? NODE_CARD_WIDTH : collapsedMediaBox.width
  const cardInnerWidth = showExpanded ? NODE_CARD_WIDTH - 20 : Math.max(1, collapsedMediaBox.width - 2)
  const resultHeight = useMemo(() => {
    if (!showExpanded) {
      return showGenerating && !showResult ? NODE_COLLAPSED_EMPTY_HEIGHT : collapsedMediaBox.height
    }
    if (showResult || showGenerating) {
      const guess = naturalSizeFromAspectRatio(aspectRatio)
      return computeResultHeight(naturalSize.w || outputWidth || guess.w, naturalSize.h || outputHeight || guess.h, cardInnerWidth)
    }
    return RESULT_EMPTY_HEIGHT
  }, [aspectRatio, cardInnerWidth, collapsedMediaBox.height, naturalSize.h, naturalSize.w, outputHeight, outputWidth, showExpanded, showGenerating, showResult])

  useEffect(() => {
    if (outputWidth > 0 && outputHeight > 0) {
      setNaturalSize((prev) => (prev.w === outputWidth && prev.h === outputHeight ? prev : { w: outputWidth, h: outputHeight }))
    }
  }, [outputHeight, outputWidth])

  const resultUrl = outputAssetId ? apparelVideoReplicationOutputContentUrl(workspaceId, outputAssetId) : null
  // V1 has no poster/first-frame-thumbnail route for this capability's own
  // output — `CanvasVideoPreview`'s `poster` prop is optional and simply
  // renders the video surface directly without a poster-first phase.
  const posterUrl: string | undefined = undefined

  const handleDownload = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!resultUrl) return
      downloadCanvasMedia(resultUrl)
    },
    [resultUrl],
  )

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields as Record<string, unknown>, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  const markStaleIfHasResult = useCallback(
    (patch: Record<string, unknown>) => {
      return hasCurrentResult ? { ...patch, needsRegeneration: true } : patch
    },
    [hasCurrentResult],
  )

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const pollTask = useCallback(
    (taskId: string) => {
      clearPoll()
      const tick = async () => {
        try {
          const live = fieldsRef.current as Record<string, unknown>
          const liveJobId = (live.jobId as string | null | undefined) ?? null
          if (!liveJobId || liveJobId !== taskId) return
          const res = await getApparelVideoReplicationTask(workspaceId, taskId)
          const mapped = mapTaskStatus(res.status)
          const liveHistory = Array.isArray(live.generationHistory) ? (live.generationHistory as ApparelVideoHistoryEntry[]) : []
          const livePrevAsset = (live.previousOutputAssetId as string | null | undefined) ?? null
          const liveW = (live.outputWidth as number | undefined) ?? outputWidth
          const liveH = (live.outputHeight as number | undefined) ?? outputHeight
          const patch: Record<string, unknown> = { status: mapped, errorMessage: res.run_state?.error_message ?? null }
          if (mapped === 'COMPLETED') {
            preferGeneratingPlaceholderRef.current = false
            if (livePrevAsset) {
              patch.generationHistory = appendHistoryEntry(liveHistory, {
                outputAssetId: livePrevAsset,
                outputWidth: liveW,
                outputHeight: liveH,
              })
            }
            patch.outputAssetId = res.output?.output_asset_id ?? null
            patch.outputWidth = res.output?.width ?? liveW
            patch.outputHeight = res.output?.height ?? liveH
            patch.previousOutputAssetId = null
            patch.needsRegeneration = false
            patch.resultInputFingerprint = (live.resultInputFingerprint as string | undefined) || materialFingerprint
          } else if (mapped === 'FAILED') {
            preferGeneratingPlaceholderRef.current = false
            patch.outputAssetId = null
            patch.previousOutputAssetId = null
            patch.needsRegeneration = true
          }
          const latestJobId = ((fieldsRef.current as Record<string, unknown>).jobId as string | null) ?? null
          if (!latestJobId || latestJobId !== taskId) return
          patchFields(patch)
          if (mapped === 'COMPLETED' || mapped === 'FAILED') {
            if (mapped === 'FAILED') {
              message.error(
                (patch.errorMessage as string) ||
                  t('apparelVideoReplication.errors.processingFailed', { defaultValue: '生成失败，请稍后重试' }),
              )
            }
            return
          }
          pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS)
        } catch {
          pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS * 2)
        }
      }
      void tick()
    },
    [clearPoll, materialFingerprint, outputHeight, outputWidth, patchFields, t, workspaceId],
  )

  const patchFieldsRef = useRef(patchFields)
  patchFieldsRef.current = patchFields
  const pollTaskRef = useRef(pollTask)
  pollTaskRef.current = pollTask

  useEffect(() => () => clearPoll(), [clearPoll])

  useEffect(() => {
    if (!jobId || !workspaceId) {
      setRunHydrating(false)
      return
    }
    if (status === 'SUBMITTING' || status === 'QUEUED' || status === 'PROCESSING') {
      pollTaskRef.current(jobId)
      setRunHydrating(false)
      return
    }
    setRunHydrating(false)
  }, [jobId, status, workspaceId])

  const runGenerate = useCallback(async () => {
    if (!workspaceId || submittingRef.current || !canGenerate) return
    if (!resolvedProviderOptionId) {
      message.error(t('apparelVideoReplication.errors.providerRequired', { defaultValue: '请选择 Provider' }))
      return
    }
    submittingRef.current = true
    setSubmitError(null)
    const selectionPersist =
      providerSelectionMode === 'EXPLICIT'
        ? buildExplicitProviderSelectionPatch(resolvedProviderOptionId)
        : buildDefaultProviderSelectionPersistPatch()
    if (hasCurrentResult) {
      preferGeneratingPlaceholderRef.current = true
      patchFields({
        generationHistory: appendHistoryEntry(generationHistory, { outputAssetId, outputWidth, outputHeight }),
        previousOutputAssetId: outputAssetId,
        status: 'SUBMITTING',
        errorMessage: null,
        ...selectionPersist,
      })
    } else {
      preferGeneratingPlaceholderRef.current = false
      patchFields({ status: 'SUBMITTING', errorMessage: null, jobId: null, ...selectionPersist })
    }
    try {
      const res = await createApparelVideoReplicationTask(workspaceId, {
        source_video_asset_id: sourceVideoAssetId as string,
        product_image_asset_ids: productImages.map((a) => a.asset_id),
        product_description: productDescription,
        model_image_asset_ids: modelImages.map((a) => a.asset_id),
        bgm_asset_id: bgmAssetId,
        resolution,
        aspect_ratio: aspectRatio,
        provider_option_id: resolvedProviderOptionId,
        canvas_node_id: id,
      })
      const mappedStatus = mapTaskStatus(res.status)
      const immediateFail = mappedStatus === 'FAILED' ? res.error_message || null : null
      patchFields({
        jobId: res.job_id,
        status: mappedStatus,
        ...selectionPersist,
        needsRegeneration: false,
        resultInputFingerprint: materialFingerprint,
        outputAssetId: null,
        errorMessage: immediateFail,
      })
      if (mappedStatus === 'FAILED') {
        preferGeneratingPlaceholderRef.current = false
        message.error(immediateFail || t('apparelVideoReplication.errors.processingFailed', { defaultValue: '生成失败，请稍后重试' }))
        return
      }
      pollTask(res.job_id)
    } catch (err) {
      preferGeneratingPlaceholderRef.current = false
      const apiErr = err as SaasApiError
      const display = apiErr.message || t('apparelVideoReplication.errors.submitFailed', { defaultValue: '提交失败，请稍后重试' })
      setSubmitError(display)
      patchFields({ status: 'FAILED', needsRegeneration: true, errorMessage: display, jobId: null, outputAssetId: null })
      message.error(display)
    } finally {
      submittingRef.current = false
    }
  }, [
    aspectRatio,
    bgmAssetId,
    canGenerate,
    generationHistory,
    hasCurrentResult,
    id,
    materialFingerprint,
    modelImages,
    outputAssetId,
    outputHeight,
    outputWidth,
    patchFields,
    pollTask,
    productDescription,
    productImages,
    providerSelectionMode,
    resolution,
    resolvedProviderOptionId,
    sourceVideoAssetId,
    t,
    workspaceId,
  ])

  const handleGenerateClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (isBusy) return
    if (!canGenerate) {
      if (!hasSourceVideo || !hasValidProductImages || !hasValidDescription) {
        message.warning(t('apparelVideoReplication.errors.invalidInput', { defaultValue: '请补全参考视频 / 商品图片 / 商品描述' }))
      }
      return
    }
    const decision = resolveRegenerationDecision({
      hasCurrentResult,
      materialInputsChanged: needsRegeneration,
      isBusy,
      isFailedRetry: status === 'FAILED' && !needsRegeneration,
    })
    if (decision === 'ask') {
      setRegenerateChoiceOpen(true)
      return
    }
    void runGenerate()
  }

  const handleOverwriteRegenerate = useCallback(() => {
    setRegenerateChoiceOpen(false)
    void runGenerate()
  }, [runGenerate])

  const handleRegenerateSeparately = useCallback(() => {
    setRegenerateChoiceOpen(false)
    nodeData.onDuplicateForRegenerate?.(id)
  }, [id, nodeData])

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }
  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const restoreHistoryEntry = (entry: ApparelVideoHistoryEntry) => {
    if (showResult && outputAssetId) {
      patchFields({
        generationHistory: appendHistoryEntry(
          generationHistory.filter((h) => h.version !== entry.version),
          { outputAssetId, outputWidth, outputHeight },
        ),
        outputAssetId: entry.outputAssetId,
        outputWidth: entry.outputWidth ?? outputWidth,
        outputHeight: entry.outputHeight ?? outputHeight,
        status: 'COMPLETED',
        needsRegeneration: false,
      })
    } else {
      patchFields({
        outputAssetId: entry.outputAssetId,
        outputWidth: entry.outputWidth ?? outputWidth,
        outputHeight: entry.outputHeight ?? outputHeight,
        status: 'COMPLETED',
        needsRegeneration: false,
        generationHistory: generationHistory.filter((h) => h.version !== entry.version),
      })
    }
    setHistoryOpen(false)
  }

  const { onClick: onMediaClick, onDoubleClick: onMediaDoubleClick } = useSingleOrDoubleClick(
    () => {
      nodeData.onExpandFunctionCardDetail?.(id)
    },
    () => {
      if (showResult && resultUrl) setPreviewOpen(true)
    },
  )

  const openSourceVideoPicker = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onOpenVideoAssetPicker?.({
      nodeId: id,
      title: hasSourceVideo
        ? t('apparelVideoReplication.sourceVideo.replace', { defaultValue: '更换参考视频' })
        : t('apparelVideoReplication.sourceVideo.select', { defaultValue: '选择参考视频' }),
      initialSelection: sourceVideoAssetId ? `upload:${sourceVideoAssetId}` : null,
    })
  }

  const openProductImagePicker = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onOpenAssetPicker?.({
      nodeId: id,
      purpose: 'generic',
      title: t('apparelVideoReplication.productImages.select', { defaultValue: '选择商品图片（1-5张）' }),
      selectionMode: 'multiple',
      maxSelection: MAX_PRODUCT_IMAGES,
      initialSelection: productImages.map((a) => a.picker_item_id || `sa:${a.asset_id}`),
      onConfirm: (selections) => {
        const next: ApparelImageRef[] = selections.map((s) => ({
          asset_id: s.assetId,
          picker_item_id: s.pickerItemId,
          display_name: s.filename || s.assetId,
        }))
        patchFields(markStaleIfHasResult({ productImages: next }))
      },
    })
  }

  const openModelImagePicker = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onOpenAssetPicker?.({
      nodeId: id,
      purpose: 'generic',
      title: t('apparelVideoReplication.modelImages.select', { defaultValue: '选择模特图片（可选，0-5张）' }),
      selectionMode: 'multiple',
      maxSelection: MAX_MODEL_IMAGES,
      initialSelection: modelImages.map((a) => a.picker_item_id || `sa:${a.asset_id}`),
      onConfirm: (selections) => {
        const next: ApparelImageRef[] = selections.map((s) => ({
          asset_id: s.assetId,
          picker_item_id: s.pickerItemId,
          display_name: s.filename || s.assetId,
        }))
        patchFields(markStaleIfHasResult({ modelImages: next }))
      },
    })
  }

  const openBgmPicker = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onOpenAudioAssetPicker?.({
      nodeId: id,
      title: bgmAssetId
        ? t('apparelVideoReplication.bgm.replace', { defaultValue: '更换背景音乐' })
        : t('apparelVideoReplication.bgm.select', { defaultValue: '选择背景音乐（可选）' }),
      binding: 'apparel_video_bgm',
      initialSelection: bgmAssetId ? `asset:${bgmAssetId}` : null,
    })
  }

  const removeBgm = (e: ReactMouseEvent) => {
    e.stopPropagation()
    patchFields(markStaleIfHasResult({ bgmAssetId: null, bgmLabel: null }))
  }

  const resolutionOptions = APPAREL_VIDEO_SUPPORTED_RESOLUTIONS.map((r) => ({ value: r, label: r }))
  const aspectRatioOptions = APPAREL_VIDEO_SUPPORTED_ASPECT_RATIOS.map((r) => ({ value: r, label: r }))

  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((opt) => {
        const available = isProviderOptionSelectable(opt)
        return {
          value: opt.provider_option_id,
          disabled: !available,
          label: opt.frontend_display_name,
          title: opt.frontend_description || opt.frontend_display_name,
        }
      }),
    [providerOptions],
  )

  const resultStatusBadge = showGenerating
    ? t('apparelVideoReplication.result.generating', { defaultValue: '生成中' })
    : status === 'FAILED'
      ? t('apparelVideoReplication.result.failed', { defaultValue: '失败' })
      : needsRegeneration && showResult
        ? t('generation.needsRegeneration')
        : showResult
          ? t('apparelVideoReplication.result.ready', { defaultValue: '已生成' })
          : t('apparelVideoReplication.result.empty', { defaultValue: '未生成' })

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--apparel-video-generator${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showExpanded)}`}
      style={{ width: cardWidth }}
      data-testid="canvas-apparel-video-node"
      data-expanded={showExpanded ? 'yes' : 'no'}
    >
      <CanvasFunctionCardHeader
        icon={def.icon}
        color={def.color}
        title={displayLabel}
        statusBadge={
          needsRegeneration && showResult && !isBusy ? (
            <span className="pcv-brief-header-badge pcv-gen-stale-badge" data-testid="apparel-video-needs-regen-badge">
              {t('generation.needsRegeneration')}
            </span>
          ) : null
        }
        actions={
          <>
            {showResult && resultUrl ? (
              <Tooltip title={mediaHeader.download} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.download} onClick={handleDownload}>
                  <DownloadOutlined />
                </button>
              </Tooltip>
            ) : null}
            <Tooltip title={t('apparelVideoReplication.history.title', { defaultValue: '历史版本' })} mouseEnterDelay={0.3}>
              <button
                type="button"
                ref={setHistoryBtnEl}
                className="pcv-media-hover-btn"
                data-testid="apparel-video-history-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  setHistoryBtnEl(e.currentTarget)
                  setHistoryOpen(true)
                }}
              >
                <HistoryOutlined />
              </button>
            </Tooltip>
            <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.duplicate} onClick={handleCopyClick}>
                <CopyOutlined />
              </button>
            </Tooltip>
            <Tooltip title={mediaHeader.delete} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" aria-label={mediaHeader.delete} onClick={handleDeleteClick}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </>
        }
      />

      {/* Result — video only */}
      <div
        className={`pcv-gen-result-area ${NODE_EXPAND_TRIGGER_CLASS}`}
        data-testid="apparel-video-result-area"
        onClick={onMediaClick}
        onDoubleClick={onMediaDoubleClick}
      >
        <div className="pcv-gen-result-section-label">
          <span>{t('apparelVideoReplication.result.label', { defaultValue: '生成结果' })}</span>
          <span className={`pcv-gen-result-status-badge${showResult && !isBusy ? ' is-ready' : ''}${showGenerating ? ' is-busy' : ''}`}>
            {resultStatusBadge}
          </span>
        </div>

        <div className="pcv-media-body" style={{ height: resultHeight }}>
          {showResult && resultUrl ? (
            <>
              <CanvasVideoPreview
                key={resultUrl}
                assetId={resultUrl}
                src={resultUrl}
                poster={posterUrl}
                className="pcv-media-image"
                onLoadedDimensions={(w, h) => {
                  if (w && h && (w !== outputWidth || h !== outputHeight)) {
                    setNaturalSize({ w, h })
                    if (!outputWidth || !outputHeight) patchFields({ outputWidth: w, outputHeight: h })
                  }
                }}
              />
              {showGenerating ? (
                <div className="pcv-tryon-result-overlay" data-testid="apparel-video-generating-overlay">
                  <LoadingOutlined spin />
                  <span>{resultStatusBadge}</span>
                </div>
              ) : null}
            </>
          ) : showGenerating ? (
            <div className="pcv-gen-result-loading" data-testid="apparel-video-generating-empty">
              <LoadingOutlined spin />
              <span>{resultStatusBadge}</span>
            </div>
          ) : (
            <div className="pcv-media-placeholder" data-testid="apparel-video-result-empty">
              <span className="pcv-gen-result-empty-icon">{def.icon}</span>
              <span className="pcv-media-placeholder-hint">
                {showExpanded
                  ? t('apparelVideoReplication.result.emptyHintExpanded', {
                      defaultValue: '请添加参考视频、商品图片与商品描述后点击「生成」',
                    })
                  : t('apparelVideoReplication.result.emptyHintCollapsed', {
                      defaultValue: '展开卡片添加素材，再点击「生成」',
                    })}
              </span>
            </div>
          )}
        </div>

        {submitError || (errorMessage && (status === 'FAILED' || status === 'COMPLETED')) ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{submitError || errorMessage}</div>
        ) : null}
      </div>

      {showExpanded ? (
        <div className="pcv-brief-body pcv-gen-input-body nodrag" onClick={(e) => e.stopPropagation()} data-testid="apparel-video-expanded-body">
          {/* Input Assets */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">
              {t('apparelVideoReplication.assets.sourceVideoLabel', { defaultValue: '参考视频（必选，1个）' })}
            </span>
            {hasSourceVideo ? (
              <div className="pcv-gen-field-chip">
                <img
                  src={videoGenAssetPosterUrl(workspaceId, sourceVideoAssetId as string)}
                  alt=""
                  className="pcv-gen-field-thumb"
                  draggable={false}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <span className="pcv-gen-field-chip-name">{sourceVideoLabel || sourceVideoAssetId}</span>
                <button type="button" className="pcv-gen-link-btn" disabled={isBusy} onClick={openSourceVideoPicker}>
                  {t('apparelVideoReplication.sourceVideo.replace', { defaultValue: '更换' })}
                </button>
              </div>
            ) : (
              <button type="button" className="pcv-brief-asset-add-btn nodrag" disabled={isBusy} onClick={openSourceVideoPicker}>
                {t('apparelVideoReplication.sourceVideo.select', { defaultValue: '选择参考视频' })}
              </button>
            )}
          </div>

          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">
              {t('apparelVideoReplication.assets.productImagesLabel', {
                defaultValue: '商品图片（必选，1-5张）',
              })}{' '}
              ({productImages.length}/{MAX_PRODUCT_IMAGES})
            </span>
            <div className="pcv-brief-asset-row">
              {productImages.map((a) => (
                <ImageChip
                  key={a.asset_id}
                  asset={a}
                  workspaceId={workspaceId}
                  disabled={isBusy}
                  onRemove={() =>
                    patchFields(
                      markStaleIfHasResult({ productImages: productImages.filter((x) => x.asset_id !== a.asset_id) }),
                    )
                  }
                />
              ))}
              {productImages.length < MAX_PRODUCT_IMAGES ? (
                <button type="button" className="pcv-brief-asset-add-btn nodrag" disabled={isBusy} onClick={openProductImagePicker}>
                  {t('apparelVideoReplication.productImages.add', { defaultValue: '添加商品图片' })}
                </button>
              ) : null}
            </div>
          </div>

          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">
              {t('apparelVideoReplication.assets.modelImagesLabel', {
                defaultValue: '模特图片（可选，未指定时使用参考视频原人物）',
              })}{' '}
              ({modelImages.length}/{MAX_MODEL_IMAGES})
            </span>
            <div className="pcv-brief-asset-row">
              {modelImages.map((a) => (
                <ImageChip
                  key={a.asset_id}
                  asset={a}
                  workspaceId={workspaceId}
                  disabled={isBusy}
                  onRemove={() =>
                    patchFields(markStaleIfHasResult({ modelImages: modelImages.filter((x) => x.asset_id !== a.asset_id) }))
                  }
                />
              ))}
              {modelImages.length < MAX_MODEL_IMAGES ? (
                <button type="button" className="pcv-brief-asset-add-btn nodrag" disabled={isBusy} onClick={openModelImagePicker}>
                  {t('apparelVideoReplication.modelImages.add', { defaultValue: '添加模特图片' })}
                </button>
              ) : null}
            </div>
          </div>

          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label pcv-brief-section-optional">
              {t('apparelVideoReplication.assets.bgmLabel', { defaultValue: '背景音乐（可选）' })}
            </span>
            {bgmAssetId ? (
              <div className="pcv-gen-field-chip">
                <span className="pcv-gen-field-chip-name">{bgmLabel || bgmAssetId}</span>
                <button type="button" className="pcv-gen-link-btn" disabled={isBusy} onClick={openBgmPicker}>
                  {t('apparelVideoReplication.bgm.replace', { defaultValue: '更换' })}
                </button>
                <button type="button" className="pcv-gen-link-btn" disabled={isBusy} onClick={removeBgm}>
                  {t('apparelVideoReplication.bgm.remove', { defaultValue: '移除' })}
                </button>
              </div>
            ) : (
              <button type="button" className="pcv-brief-asset-add-btn nodrag" disabled={isBusy} onClick={openBgmPicker}>
                {t('apparelVideoReplication.bgm.select', { defaultValue: '选择背景音乐' })}
              </button>
            )}
          </div>

          {/* Parameters */}
          <div className="pcv-gen-params-header">
            <span className="pcv-brief-section-label">{t('apparelVideoReplication.params.label', { defaultValue: '生成参数' })}</span>
          </div>
          <div className="pcv-gen-params-primary pcv-tryon-params-row">
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">{t('apparelVideoReplication.params.resolution', { defaultValue: '分辨率' })}</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                data-testid="apparel-video-resolution-select"
                value={resolution}
                disabled={isBusy}
                options={resolutionOptions}
                onChange={(v) => patchFields(markStaleIfHasResult({ resolution: v }))}
                popupMatchSelectWidth={false}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">{t('apparelVideoReplication.params.aspectRatio', { defaultValue: '画面比例' })}</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                data-testid="apparel-video-aspect-ratio-select"
                value={aspectRatio}
                disabled={isBusy}
                options={aspectRatioOptions}
                onChange={(v) => patchFields(markStaleIfHasResult({ aspectRatio: v }))}
                popupMatchSelectWidth={false}
              />
            </div>
          </div>

          {/* Prompt — 商品描述 (required) */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">{t('apparelVideoReplication.description.label', { defaultValue: '商品描述（必填）' })}</span>
            <textarea
              className="pcv-brief-textarea nodrag"
              data-testid="apparel-video-description"
              placeholder={t('apparelVideoReplication.description.placeholder', {
                defaultValue: '请描述商品的材质、颜色、款式与卖点，Kling 将据此复刻参考视频的展示方式……',
              })}
              value={productDescription}
              maxLength={APPAREL_VIDEO_PRODUCT_DESCRIPTION_MAX_LENGTH}
              disabled={isBusy}
              onChange={(e) => patchFields(markStaleIfHasResult({ productDescription: e.target.value }))}
            />
            <span className="pcv-brief-textarea-counter">
              {productDescription.length}/{APPAREL_VIDEO_PRODUCT_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>

          {/* Footer — Provider | Generate */}
          <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()} data-testid="apparel-video-footer">
            <CanvasSelect
              size="small"
              className="pcv-gen-field-select pcv-gen-footer-select nodrag"
              data-testid="apparel-video-provider-select"
              aria-label={t('apparelVideoReplication.provider.label', { defaultValue: 'Provider' })}
              value={resolvedProviderOptionId ?? undefined}
              disabled={isBusy || providerOptionsError || providerSelectOptions.length === 0}
              placeholder={
                providerOptionsError
                  ? t('apparelVideoReplication.provider.loadFailed', { defaultValue: '加载失败' })
                  : t('apparelVideoReplication.provider.placeholder', { defaultValue: '请选择 Provider' })
              }
              options={providerSelectOptions}
              onChange={(v) => patchFields(markStaleIfHasResult(buildExplicitProviderSelectionPatch(v)))}
              popupMatchSelectWidth={false}
              placement="bottomLeft"
              getPopupContainer={() => document.body}
            />
            <button
              type="button"
              ref={runBtnRef}
              className="pcv-gen-footer-generate-btn"
              data-testid="apparel-video-generate-btn"
              disabled={!canGenerate}
              onClick={handleGenerateClick}
            >
              {isBusy ? <LoadingOutlined spin /> : <ThunderboltOutlined />}{' '}
              {isBusy
                ? t('apparelVideoReplication.actions.generating', { defaultValue: '生成中…' })
                : t('apparelVideoReplication.actions.generate', { defaultValue: '生成' })}
            </button>
          </div>
          {explicitProviderInvalid ? (
            <div className="pcv-gen-field-hint" data-testid="apparel-video-provider-unavailable">
              {t('apparelVideoReplication.provider.currentUnavailable', {
                defaultValue: '当前 Provider 已不可用，请重新选择。',
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <CanvasActionPopover
        anchorEl={historyBtnEl}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('apparelVideoReplication.history.title', { defaultValue: '生成历史' })}
        width={280}
      >
        {generationHistory.length === 0 ? (
          <div className="pcv-gen-history-empty">{t('apparelVideoReplication.history.empty', { defaultValue: '暂无历史版本' })}</div>
        ) : (
          <div className="pcv-gen-history-list" data-testid="apparel-video-history-list">
            {[...generationHistory].reverse().map((h) => (
              <button
                key={`${h.version}-${h.outputAssetId}`}
                type="button"
                className="pcv-gen-history-item nodrag"
                onClick={() => restoreHistoryEntry(h)}
              >
                <span className="pcv-gen-history-version">V{h.version}</span>
                <span className="pcv-gen-history-meta">{h.completedAt ? new Date(h.completedAt).toLocaleString() : ''}</span>
              </button>
            ))}
          </div>
        )}
      </CanvasActionPopover>

      <CanvasActionPopover
        anchorEl={runBtnRef.current}
        open={regenerateChoiceOpen}
        onClose={() => setRegenerateChoiceOpen(false)}
        title={t('apparelVideoReplication.regenerate.title', { defaultValue: regenerate.title })}
        width={300}
      >
        <p className="pcv-action-popover-desc">{t('generation.regenerateDescription')}</p>
        <div className="pcv-action-popover-actions" data-testid="apparel-video-regen-decision">
          <button type="button" className="pcv-action-popover-btn nodrag" onClick={handleRegenerateSeparately}>
            {t('apparelVideoReplication.regenerate.separate', { defaultValue: regenerate.separate })}
          </button>
          <button type="button" className="pcv-action-popover-btn pcv-action-popover-btn--primary nodrag" onClick={handleOverwriteRegenerate}>
            {t('apparelVideoReplication.regenerate.overwrite', { defaultValue: regenerate.overwrite })}
          </button>
        </div>
      </CanvasActionPopover>

      {previewOpen && showResult && resultUrl ? (
        <MediaLightbox open kind="video" src={resultUrl} assetId={resultUrl} onClose={() => setPreviewOpen(false)} />
      ) : null}
    </div>
  )
}

export default memo(ApparelVideoReplicationNodeImpl)
